import { UserClaims } from '@okta/okta-auth-js';
import * as Response from '../response/firebase.response';
import { AddonModule, Bsl, ProductTier, THttpMethod } from '../types';

export type TRequestHeaders = Headers | BslProxyHeaders;

export interface Headers {
  [key: string]: string;
}

export interface BslProxyHeaders extends Headers {
  reqUrl: string;
  method: THttpMethod;
}

// Remember to add any new request types to this type
export type TRequestBody =
  | Document
  | Collection
  | EndpointAccessData
  | OrganizationData
  | Organization
  | OrganizationLinks
  | SiteData
  | Site
  | SiteBinding
  | SiteBindingData
  | UserData
  | User
  | RoleData
  | Role
  | LinkData
  | Link
  | BslProxyRequest
  | ConvertRequest;

/** Wrapper for all Firebase requests */
export interface RequestBody<T extends TRequestBody> {
  body?: T;
  headers?: Headers;
  oktaToken?: string;
}

export interface FirebaseTokenRequest {
  accessToken: string | null;
  claims: UserClaims | null;
  org: Bsl.Provisioning.AuthorizedOrganization;
}

// TODO Remove this type once token functions using it can be safely removed
export interface FirebaseTokenRequest_Old {
  accessToken: string | null;
  claims: UserClaims | null;
  /** short name or id */
  org?: string;
}

/** Wrapper for BslProxy requests */
export interface BslProxyRequestBody<T extends TRequestBody> extends RequestBody<T> {
  headers: BslProxyHeaders;
}

export interface Document {
  id: string;
}

export interface Collection {
  name: string;
}

export interface DocumentAccess {
  readRoleIds: string[];
  updateRoleIds?: string[];
  deleteRoleIds?: string[];
}

export interface OrganizationLinks {
  filterByUserRoles?: boolean;
}

export interface OrganizationData {
  name: string;
  address?: Response.Address;
  phoneNumber?: string;
  productTier?: ProductTier;
  addonModules?: AddonModule[];
  bslOrgId?: string;
}

export interface Organization extends OrganizationData, Document {}

export interface SiteData {
  name: string;
  groups?: string[];
  organizationId?: string;
  bslSiteId?: string;
  timeZone?: string;
}

export interface Site extends SiteData, Document {}

export interface UserData {
  name?: string;
  email: string;
  sites?: SiteBindingData[];
}

export interface User extends UserData, Document {}

export interface SiteBindingData {
  userId: string;
  siteId: string;
  roleIds?: string[];
}

export interface SiteBinding extends SiteBindingData {}

export interface RoleData {
  name: string;
  description?: string;
  permissionIds?: string[];
}

export interface Role extends RoleData, Document {}

export interface PermissionData extends Response.PermissionData {}

export interface Permission extends PermissionData, Document {}

export interface LinkData extends DocumentAccess {
  displayName: string;
  url: string;
}

export interface Link extends LinkData, Document {}

export interface EndpointAccessData {
  handlerName: string;
  permissionIds: string[];
}

export interface EndpointAccess extends EndpointAccessData, Document {}

export interface RecordLogout {
  name: string;
  email: string;
  organizationId: string;
  siteId: string;
}

export enum HttpVerb {
  GET = 'get',
  PUT = 'put',
  PATCH = 'patch',
  POST = 'post',
  DELETE = 'delete',
}

export interface BslProxyRequest {
  proxy: string;
  pathSegments: string[];
  queryParams?: Array<MultiQueryParam | SingleQueryParam>;
  enterpriseUnit?: string;
  enterpriseUnitGroup?: string;
  validateSiteIds?: boolean;
  correlationId?: string;
  additionalHeaders?: Array<Header>;
  payload?: unknown;
  verb: HttpVerb;
  localOverride?: { port: number };
}

export interface BslProxyRequestNcrId extends BslProxyRequest {
  oktaJwt: string;
}

interface QueryParam {
  kind: string;
  key: string;
}

export interface SingleQueryParam extends QueryParam {
  kind: 'single';
  value: string;
}

export interface MultiQueryParam extends QueryParam {
  kind: 'multi';
  value: string[];
}

export interface Header {
  key: string;
  value: string;
}
export interface ConvertRequest {
  collection: string;
}
